import React, { useContext } from "react";
import { SizedPrice, Change, Variant } from "@bloomberg/media-ui-markets";
import { ScreenContext } from "@foundation/screen";
import styles from "./wrappedDailySnapshot.module.css";
import { GainLoss, CalculatedValue } from "~types/octagon";

type WrappedDailySnapshotProps = {
  todayGainLoss: GainLoss;
  totalGainLoss: GainLoss;
  totalValue: CalculatedValue;
  totalCost: CalculatedValue;
};

type SnapshotMetricProps = {
  title: string;
  value: number;
  percentChange: number;
  showSign?: boolean;
};

const TOTAL_GAIN_LOSS_HEADER = "Total Gain / Loss";

function SnapshotMetric({
  title,
  value,
  percentChange,
  showSign = false,
}: SnapshotMetricProps) {
  return (
    <div className={styles.metricContainer}>
      <p className={styles.metricTitle}>{title}</p>
      <span className={styles.priceDisplay}>
        <div className={styles.watchlistSnapshotMetrics}>
          <div className={styles.metricValueWrapper}>
            {showSign && value >= 0 && <span className={styles.price}>+</span>}
            <SizedPrice
              price={value}
              size="large"
              className={styles.metricValue}
            />
          </div>
          {(percentChange || percentChange !== 0) && (
            <Change
              className={styles.change}
              name="change"
              percentChange={percentChange}
              size="large"
              variant={Variant.refresh2024}
            />
          )}
        </div>
      </span>
    </div>
  );
}

export function WrappedDailySnapshot({
  todayGainLoss,
  totalGainLoss,
  totalValue,
  totalCost,
}: WrappedDailySnapshotProps) {
  const {
    breakpoints: { tablet, smallDesktop, largeDesktop },
  } = useContext(ScreenContext);
  const isMobile = !tablet && !smallDesktop && !largeDesktop;

  return (
    <div className={styles.snapshot}>
      <div className={styles.snapshotTitle}>Daily Snapshot</div>

      <div className={styles.snapshotGrid}>
        <div className={styles.snapshotGridRow}>
          <SnapshotMetric
            title="Today’s Gain / Loss"
            value={todayGainLoss.value}
            percentChange={todayGainLoss.percentChange}
            showSign
          />
          <SnapshotMetric
            title={isMobile ? TOTAL_GAIN_LOSS_HEADER : "Value"}
            value={isMobile ? totalGainLoss.value : totalValue.value}
            percentChange={isMobile ? totalGainLoss.percentChange : 0}
          />
        </div>
        <div className={styles.snapshotGridRow}>
          <SnapshotMetric
            title={isMobile ? "Value" : TOTAL_GAIN_LOSS_HEADER}
            value={isMobile ? totalValue.value : totalGainLoss.value}
            percentChange={isMobile ? 0 : totalGainLoss.percentChange}
            showSign
          />
          <SnapshotMetric
            title="Cost"
            value={totalCost.value}
            percentChange={0}
          />
        </div>
      </div>
    </div>
  );
}
